import { makeAutoObservable, runInAction, when } from 'mobx';
import { msalClient } from '.';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { datadogRum } from '@datadog/browser-rum';

const MSAL_SCOPES = ['User.Read'];

class AuthStore {
	scope = null;
	account = null;
	accessToken = null;
	expiresOn = null;
	isLoading = true; // Prevents unnecessary re-renders before auth is ready

	constructor() {
		makeAutoObservable(this);
	}

	async initializeAuth() {
		this.setEnv();

		try {
			await msalClient.initialize();

			const accounts = msalClient.getAllAccounts();

			if (accounts.length > 0) {
				this.account = accounts[0];
				msalClient.setActiveAccount(this.account);
				await this.getAccessToken(); // Fetch the access token
			}
		} catch (error) {
			datadogRum.addError(error, {
				source: 'authorization',
				operation: 'initialize',
			});
			// Fall through — user sees the unauthorized screen and can retry.
		} finally {
			// Always set loading to false, whether accounts exist or not
			// This will set for the user Unauthorized link
			this.isLoading = false;
		}
	}

	async login() {
		this.isLoading = true;

		try {
			const loginResponse = await msalClient.loginPopup({
				scopes: [this.scope] || MSAL_SCOPES,
			});

			this.account = loginResponse.account;
			msalClient.setActiveAccount(this.account);

			await this.getAccessToken();
			await this.getAvatar();

			this.isLoading = false;
		} catch (error) {
			datadogRum.addError(error, {
				source: 'authorization',
				operation: 'login',
			});
			console.error('Login failed:', error);
		}
	}

	async getAccessToken(forceRefresh = false) {
		if (!this.account) return null;

		this.isLoading = true;

		try {
			const tokenResponse = await msalClient.acquireTokenSilent({
				account: this.account,
				scopes: [this.scope],
				forceRefresh,
			});

			runInAction(() => {
				this.accessToken = tokenResponse.accessToken;
				this.expiresOn = tokenResponse.expiresOn?.getTime() ?? null;
			});
		} catch (error) {
			if (error instanceof InteractionRequiredAuthError) {
				try {
					const response = await msalClient.acquireTokenPopup({
						account: this.account,
						scopes: [this.scope] || MSAL_SCOPES,
					});

					runInAction(() => {
						this.accessToken = response.accessToken;
					});
					return;
				} catch (popupError) {
					datadogRum.addError(popupError, {
						source: 'authorization',
						operation: 'acquireToken',
						stage: 'interactive-fallback',
					});
					this.accessToken = null;
					return;
				}
			}
			datadogRum.addError(error, {
				source: 'authorization',
				operation: 'acquireToken',
				stage: 'silent',
			});
			this.accessToken = null;
		} finally {
			this.isLoading = false;
		}
	}

	isTokenExpiredSoon() {
		const buffer = 5 * 60 * 1000;
		return !this.expiresOn || Date.now() >= this.expiresOn - buffer;
	}

	async refreshToken() {
		if (!this.accessToken || this.isTokenExpiredSoon()) {
			await this.getAccessToken(true);
		}
	}

	ensureIsUserAuthenticated = async () => {
		if (this.isLoading) {
			await when(() => !this.isLoading);
		}

		if (!this.account) {
			return false;
		}

		return true;
	};

	get roles() {
		return this.account.idTokenClaims?.roles;
	}

	async logout() {
		this.isLoading = true;

		try {
			await msalClient.logoutRedirect({ onRedirectNavigate: () => false });

			runInAction(() => {
				this.account = null;
				this.accessToken = null;
			});
		} finally {
			this.isLoading = false;
		}
	}

	get needsLogin() {
		return !this.isLoading && this.account === null;
	}

	get isAuthStatusLoading() {
		return this.isLoading;
	}

	async getAvatar() {
		if (this.accessToken === null) return;
		try {
			const photoResponse = await fetch('https://graph.microsoft.com/v1.0/me/photo/$value', {
				headers: {
					Authorization: `Bearer ${this.accessToken}`,
				},
			});

			// If successful, you’ll get the image as a blob
			const photoBlob = await photoResponse.blob();
			const photoUrl = URL.createObjectURL(photoBlob);
		} catch (error) {
			console.error('Get avatar failed', error);
		}
	}

	setEnv = () => {
		const env = process.env.REACT_APP_ENV;
		switch (env) {
			case 'stg':
				this.scope = `api://fxt.ops.crew.dispatch.crew-portal.stg/user_impersonation`;
				return;
			case 'prod':
				this.scope = `api://fxt.ops.crew.dispatch.crew-portal.prod/user_impersonation`;
				return;
			default:
				this.scope = `api://fxt.ops.crew.dispatch.crew-portal.dev/user_impersonation`;
				return;
		}
	};
}

export const authStore = new AuthStore();
